/**
 * Bir run'in SSE akisini okuyup `streamStore`u besleyen surucu.
 *
 * ## `fetch` + `ReadableStream`, `EventSource` DEGIL
 *
 * Gerekce `lib/sse.ts` docstring'inde. Ozetle: asil senaryo "kullanici
 * sekmeyi kapatiyor" ve temiz bir sayfa yuklemesinde gonderilecek
 * `Last-Event-ID` yok - resume zaten KALICI STATE'ten gelmek zorunda. Yani
 * `?after=` nasilsa yaziliyor. Karsiliginda `fetch` HTTP status kodu veriyor
 * (401/403/404/429 ayirt edilebiliyor), backoff kontrol edilebiliyor ve
 * offline'da duraklatilabiliyor.
 *
 * ## Yeniden baglanma `after=lastSeq` ile
 *
 * Sunucu `after=0`i "simdiye kadar olan her sey" olarak yorumluyor, yani
 * SOGUK yeniden baglanma ile SICAK reconnect BIREBIR AYNI kod yolu. Bu
 * tasarimin asil kazanci: burada iki ayri dal yok.
 */

import { i18next } from '../i18n'
import { notifyUnauthorized } from './api'
import { markConnected, markLost, markRetrying } from './connection'
import { decodeData, parseFrames } from './sse'
import * as store from './streamStore'

/** Backoff: 8 denemeden sonra sessizce sonsuz donmek yerine vazgeciyoruz. */
export const MAX_ATTEMPTS = 8

export interface StreamHandle {
  /** Akisi durdurur (uretimi DEGIL - o sunucuda devam ediyor). */
  abort(): void
  /** Terminal olana veya vazgecilene kadar bekler. */
  done: Promise<void>
}

export interface StreamCallbacks {
  /** Terminal `done` cercevesi geldi. */
  onDone?(status: string, messageId: string | null): void
  /** Yeniden baglanma denemeleri tukendi. */
  onGiveUp?(): void
  /**
   * Store'a metin eklendi.
   *
   * LIDER sekme bunu diger sekmelere yayinliyor; takipciler hicbir baglanti
   * acmadan ayni metni goruyor. Store'a yazma zaten yapilmis oluyor -
   * bu yalnizca bir BILDIRIM, veri yolu degil.
   */
  onDelta?(text: string, seq: number): void
  /** Kuyruk pozisyonu degisti (sekmeler arasi yayin icin). */
  onQueue?(position: number | null): void
  /**
   * Sunucu bir arac cagrisini ISTEMCIYE devretti.
   *
   * Yalnizca akisi gercekten acan (lider) sekmede tanimli: takipci
   * sekmelerin ayni kodu calistirmasi bosa is olurdu.
   */
  onToolRequest?(request: ToolRequest): void
}

export interface ToolRequestAttachment {
  id: string
  filename: string
  mime: string
}

export interface ToolRequest {
  id: string
  tool: string
  code: string
  /** Bu turde run_python sandbox'ina koprulenecek ekler (varsa). */
  attachments?: ToolRequestAttachment[]
  /** Sandbox'in URETEBILECEGI dosya sayisi/boyutu siniri (sunucudan). */
  maxOutputFiles?: number
  maxOutputFileBytes?: number
  /** YEREL araclar (`local_*`) icin modelin verdigi argumanlar. */
  args?: Record<string, unknown>
}

/**
 * Akisi baslatir ve store'u besler.
 *
 * DIKKAT: bu fonksiyon bir React bileseninin ICINDE `useEffect` ile
 * baslatilmamali. Akis `<StreamManager>`a ait olmali - baska bir sohbete
 * gitmek veya sidebar'i kapatmak bir uretimi YIKMAMALI. Naif bir
 * `useEffect(() => { stream(); return abort }, [convId])` bu kisiti ihlal
 * eder ve o hata dev'de gorunmez, kullanimda cildirtici olur.
 */
export function streamRun(runId: string, callbacks: StreamCallbacks = {}): StreamHandle {
  const controller = new AbortController()
  const done = pump(runId, controller, callbacks)
  return { abort: () => controller.abort(), done }
}

async function pump(
  runId: string,
  controller: AbortController,
  callbacks: StreamCallbacks,
): Promise<void> {
  let attempt = 0

  while (!controller.signal.aborted) {
    try {
      const terminal = await readOnce(runId, controller.signal, callbacks)
      if (terminal) return
      // Akis terminal olmadan bitti (sunucu kapatti): yeniden baglan.
      attempt += 1
    } catch (error) {
      if (controller.signal.aborted) return
      if (error instanceof ReauthNeededError) {
        // Modal acildi. Okuyucuyu birakiyoruz; yeniden dogrulama sonrasi
        // `StreamManager` bu run'i TEKRAR devraliyor.
        callbacks.onGiveUp?.()
        return
      }
      if (error instanceof FatalStreamError) {
        store.setError(runId, error.message)
        store.finish(runId)
        return
      }
      attempt += 1
    }

    if (attempt >= MAX_ATTEMPTS) {
      // Sessizce sonsuza kadar donmek EN KOTU secenek: kullanici artik acik
      // bir "Tekrar dene" gormek zorunda.
      markLost()
      store.setError(runId, i18next.t('stream.error.cannotConnect'))
      callbacks.onGiveUp?.()
      return
    }
    // Yeniden baglanma ARAYUZE bildiriliyor. Bu olmadan kullanicinin gordugu
    // sey "yanit bir yerde durdu ve hicbir sey olmuyor" - uygulama calisiyor
    // ama bozuk sanildigi icin sayfa yenileniyor.
    markRetrying(attempt)
    await sleep(backoffMs(attempt), controller.signal)
  }
}

/** Kalici hata: yeniden denemek anlamsiz (ornegin 404). */
class FatalStreamError extends Error {}

/**
 * Oturum bitti: yeniden dogrulamadan sonra DEVRALINABILIR.
 *
 * `FatalStreamError`dan ayri: o "vazgec", bu "beklemede". Okuyucu birakiliyor
 * ama run terminal SAYILMIYOR ve store'a hata YAZILMIYOR - modal zaten ne
 * oldugunu soyluyor.
 */
class ReauthNeededError extends Error {}

/** Donus: terminal cerceve gorulduyse `true`. */
async function readOnce(
  runId: string,
  signal: AbortSignal,
  callbacks: StreamCallbacks,
): Promise<boolean> {
  const after = store.getBuffer(runId).lastSeq
  const response = await fetch(`/api/runs/${runId}/stream?after=${after}`, {
    signal,
    credentials: 'same-origin',
    headers: { Accept: 'text/event-stream' },
  })

  if (response.status === 404) {
    throw new FatalStreamError(i18next.t('stream.error.notFound'))
  }
  if (response.status === 401 || response.status === 403) {
    // Oturum bitti. Yeniden dogrulama modal'ini ACIYORUZ ve `SessionProvider`
    // basarili dogrulamadan sonra bu run'i yeniden devraliyor - `after=
    // lastSeq` sayesinde modal ACIKKEN uretilen token'lar tek bir ANINDA
    // `resume` cercevesiyle geliyor. Kullanici ne yerini, ne draft'ini, ne
    // yanitini kaybediyor.
    //
    // `FatalStreamError` DEGIL: bu kurtarilabilir. Hata mesaji da
    // yazilmiyor - modal zaten ne oldugunu soyluyor ve mesaj balonuna kirmizi
    // bir satir eklemek yaniti bozulmus gibi gosterirdi.
    notifyUnauthorized()
    throw new ReauthNeededError('oturum doğrulanamadı')
  }
  if (!response.ok || !response.body) {
    throw new Error(`stream ${response.status}`)
  }

  // Yanit geldi: baglanti calisiyor. Banner varsa kalkiyor.
  markConnected()

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) return false
    buffer += decoder.decode(value, { stream: true })
    const parsed = parseFrames(buffer)
    buffer = parsed.rest
    for (const frame of parsed.frames) {
      if (applyFrame(runId, frame.event, frame.data, frame.id, callbacks)) {
        // Terminal: okuyucuyu birak, baglantiyi kapat.
        await reader.cancel().catch(() => undefined)
        return true
      }
    }
  }
}

/**
 * Tek bir SSE cercevesini store'a uygular. Donus: cerceve TERMINAL mi.
 *
 * Ag katmanindan AYRI ve export edilmis: olay sozlugunun tamami (token,
 * resume, meta, state, toolcard, info, error, stats, bilinmeyen) boylece
 * `fetch` sahtelemeden, zamanlama olmadan test edilebiliyor.
 */
export function applyFrame(
  runId: string,
  event: string,
  data: string,
  id: number | null,
  callbacks: StreamCallbacks,
): boolean {
  const seq = id ?? store.getBuffer(runId).lastSeq

  switch (event) {
    case 'message': {
      // Isimsiz olay = token. Sunucu metni JSON olarak gonderiyor: ham bir
      // satir sonu SSE cercevesini ortadan bolerdi.
      const text = decodeData<string>(data, '')
      store.appendText(runId, text, seq)
      callbacks.onDelta?.(text, seq)
      return false
    }
    case 'resume': {
      // Birlestirilmis catch-up: 3000 karakter tek cercevede geliyor ve
      // ANINDA commit ediliyor (yeniden daktilo edilmiyor).
      const payload = decodeData<{ text?: string; seq_to?: number }>(data, {})
      store.commitResume(runId, payload.text ?? '', payload.seq_to ?? seq)
      // Takipci sekme icin: `resume` metni de yayinlaniyor. Onlarda
      // `appendText` olarak uygulaniyor - `commitResume` ile arasindaki tek
      // fark daktilo tabani ve takipcide zaten animasyon gerekmiyor.
      callbacks.onDelta?.(payload.text ?? '', payload.seq_to ?? seq)
      return false
    }
    case 'meta': {
      const payload = decodeData<{ model?: string }>(data, {})
      store.setMeta(runId, { model: payload.model ?? null, seq })
      return false
    }
    case 'state': {
      const payload = decodeData<{ state?: string; position?: number }>(data, {})
      const position = payload.state === 'queued' ? (payload.position ?? null) : null
      store.setQueuePosition(runId, position)
      callbacks.onQueue?.(position)
      return false
    }
    case 'toolcard': {
      // Bant DISI kart: token metniyle taklit EDILEMEZ. Sentinel bicimine
      // ceviriyoruz cunku render hatti (segments.ts) onu zaten anliyor ve
      // kartin metin icindeki YERI korunmus oluyor.
      const payload = decodeData<Record<string, unknown> | null>(data, null)
      if (payload) {
        // Gercek kart geldi: canli taslak/ilerleme karti yerini ona birakiyor.
        store.setLive(runId, null)
        store.appendText(runId, `\n\n[[TOOLCARD]]${JSON.stringify(payload)}[[/TOOLCARD]]\n\n`, seq)
      }
      return false
    }
    case 'tool_draft': {
      // Model arac cagrisini YAZARKEN (kod, gorsel talimati) - kalici degil,
      // yalnizca "ilerliyor" gostergesi. Sira numarasi (`seq`) kaydedilmiyor:
      // bu olaylar `run_chunks`ta yok, yeniden baglanma onlardan devam etmez.
      const payload = decodeData<Partial<store.LiveTool> | null>(data, null)
      if (payload?.tool) store.setLive(runId, payload)
      return false
    }
    case 'tool_progress': {
      const payload = decodeData<Partial<store.LiveTool> | null>(data, null)
      if (payload) store.setLive(runId, payload)
      return false
    }
    case 'tool_request': {
      // Sunucu kod calistirmayi ISTEMCIYE devrediyor: kod tarayicida
      // Pyodide ile kosuyor (gerekce `lib/pyodide.ts`de). Bu cerceve
      // store'a HICBIR SEY yazmiyor - kullaniciya gosterilecek metin
      // degil, yapilacak bir is.
      //
      // YALNIZCA lider sekme ustleniyor: `onToolRequest` yalnizca akisi
      // gercekten acan sekmede tanimli. Aksi halde dort sekme ayni kodu
      // calistirip ayni sonucu gonderirdi - ilki kazanir, ucu bosa
      // 13.5 MB indirip CPU yakar.
      const payload = decodeData<{
        id?: string
        tool?: string
        code?: string
        attachments?: ToolRequestAttachment[]
        max_output_files?: number
        max_output_file_bytes?: number
        args?: Record<string, unknown>
      }>(data, {})
      if (payload.id) {
        callbacks.onToolRequest?.({
          id: payload.id,
          tool: payload.tool ?? '',
          code: payload.code ?? '',
          attachments: payload.attachments,
          maxOutputFiles: payload.max_output_files,
          maxOutputFileBytes: payload.max_output_file_bytes,
          args: payload.args,
        })
      }
      return false
    }
    case 'info': {
      store.setQueuePosition(runId, null)
      store.setMeta(runId, { seq })
      return false
    }
    case 'error': {
      // Sunucu kaba bir mesaj gonderiyor (ham exception metni DEGIL).
      store.setError(
        runId,
        decodeData<string>(data, i18next.t('stream.error.generic')),
      )
      return false
    }
    case 'stats': {
      store.setMeta(runId, { seq })
      return false
    }
    case 'done': {
      const payload = decodeData<{ status?: string; message_id?: string }>(data, {})
      store.finish(runId)
      callbacks.onDone?.(payload.status ?? 'done', payload.message_id ?? null)
      return true
    }
    default:
      // Bilinmeyen olay: SESSIZCE atlaniyor. Vanilla arayuzun hatasi tam
      // buradaydi - bilinmeyen olaylarin JSON'unu token metnine enjekte
      // ediyordu.
      return false
  }
}

/** Ustel backoff + jitter. Jitter, coklu sekmenin ayni anda vurmasini onluyor. */
export function backoffMs(attempt: number): number {
  const base = Math.min(500 * 2 ** (attempt - 1), 15_000)
  return base + Math.floor(Math.random() * 250)
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}
